export const DASHBOARD_SYMBOLS = ["ES", "NQ", "GC", "CL", "HG"] as const;

export type DashboardSymbol = typeof DASHBOARD_SYMBOLS[number];
export type DashboardSession = "asia" | "us" | "all";
export type DashboardFreshness = "fresh" | "stale" | "missing";

export const LIVE_INTEL_STALE_AFTER_MS = 10 * 60 * 1000;
export const SIGNAL_ROW_STALE_AFTER_MS = 60 * 60 * 1000;

export type DashboardDataStatus = "live" | "stale" | "missing";

export interface DashboardFreshnessInfo {
  status: DashboardFreshness;
  ageMs: number | null;
  label: string;
}

export function rootSymbol(symbol: string | null | undefined): string {
  return (symbol || "").replace(/[A-Z]\d+$/, "").toUpperCase();
}

export function isDashboardSymbol(symbol: string): symbol is DashboardSymbol {
  return (DASHBOARD_SYMBOLS as readonly string[]).includes(symbol);
}

export function isLiveSnapshotAgent(agent: string | null | undefined): boolean {
  return (agent || "").trim().toLowerCase() === "live";
}

export function freshnessFromTimestamp(
  timestampMs: number | null | undefined,
  staleAfterMs: number,
  nowMs = Date.now(),
): DashboardFreshnessInfo {
  if (timestampMs === null || timestampMs === undefined || !Number.isFinite(timestampMs)) {
    return { status: "missing", ageMs: null, label: "missing timestamp" };
  }

  const ageMs = Math.max(0, nowMs - timestampMs);
  return {
    status: ageMs > staleAfterMs ? "stale" : "fresh",
    ageMs,
    label: formatAge(ageMs),
  };
}

export function formatAge(ageMs: number): string {
  if (ageMs < 60_000) return "just now";
  if (ageMs < 3_600_000) return `${Math.round(ageMs / 60_000)}m ago`;
  if (ageMs < 86_400_000) return `${Math.round(ageMs / 3_600_000)}h ago`;
  return `${Math.round(ageMs / 86_400_000)}d ago`;
}

export function dashboardDataStatusFromFreshness(
  payloadStatus: string | null | undefined,
  freshness: DashboardFreshnessInfo,
): DashboardDataStatus {
  // ``current_price_status`` is computed by the uploader at snapshot time. A
  // snapshot that was fresh seven hours ago is stale now, so recency from the
  // timestamp wins over the stored status. If recency is fresh, preserve a
  // producer-reported stale/missing price source because that means the hub
  // itself could not provide a fresh price at upload time.
  if (freshness.status === "stale" || freshness.status === "missing") {
    return freshness.status;
  }
  if (payloadStatus === "stale" || payloadStatus === "missing") {
    return payloadStatus;
  }
  return "live";
}

export interface LatestRowLike {
  symbol?: string | null;
  created_at?: number | null;
  _creationTime?: number | null;
  date?: string | null;
}

export function latestSymbolRows<T extends LatestRowLike>(
  rows: readonly T[],
  options: { staleAfterMs?: number; nowMs?: number } = {},
): Record<DashboardSymbol, (T & { freshness: DashboardFreshnessInfo }) | null> {
  const staleAfterMs = options.staleAfterMs ?? SIGNAL_ROW_STALE_AFTER_MS;
  const nowMs = options.nowMs ?? Date.now();
  const result = Object.fromEntries(DASHBOARD_SYMBOLS.map((sym) => [sym, null])) as Record<DashboardSymbol, (T & { freshness: DashboardFreshnessInfo }) | null>;

  for (const row of rows) {
    const root = rootSymbol(row.symbol);
    if (!isDashboardSymbol(root)) continue;

    const sym = root as DashboardSymbol;
    const timestamp = row.created_at ?? row._creationTime ?? null;
    const current = result[sym];
    const currentTs = current?.created_at ?? current?._creationTime ?? -Infinity;
    if (timestamp !== null && timestamp !== undefined && Number.isFinite(timestamp) && timestamp <= currentTs) continue;

    result[sym] = {
      ...row,
      freshness: freshnessFromTimestamp(timestamp, staleAfterMs, nowMs),
    };
  }

  return result;
}
